import pdfMake from 'addthaifont-pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import buddhistEra from 'dayjs/plugin/buddhistEra';

dayjs.locale('th');
dayjs.extend(buddhistEra);

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

const ORANGE = '#E87722';
const DARK = '#1a1a1a';
const GRAY = '#555555';
const LIGHT_GRAY = '#f5f5f5';
const BORDER_COLOR = '#dddddd';

// ---- Thai baht text ----
function bahtText(amount) {
  const num = parseFloat(amount) || 0;
  const [intStr, decStr = '00'] = num.toFixed(2).split('.');
  const satang = parseInt(decStr, 10);

  const digits = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
  const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];

  const convertBlock = (n) => {
    if (n === 0) return '';
    const s = String(n);
    let result = '';
    for (let i = 0; i < s.length; i++) {
      const d = parseInt(s[i], 10);
      const pos = s.length - 1 - i;
      if (d === 0) continue;
      if (pos === 1 && d === 1) result += 'สิบ';
      else if (pos === 1 && d === 2) result += 'ยี่สิบ';
      else result += digits[d] + positions[pos];
    }
    return result;
  };

  const intNum = parseInt(intStr, 10);
  if (intNum === 0 && satang === 0) return 'ศูนย์บาทถ้วน';

  let text = '';
  if (intNum >= 1000000) {
    text += convertBlock(Math.floor(intNum / 1000000)) + 'ล้าน';
    text += convertBlock(intNum % 1000000);
  } else {
    text += convertBlock(intNum);
  }
  text += 'บาท';
  text += satang === 0 ? 'ถ้วน' : convertBlock(satang) + 'สตางค์';
  return text;
}

// ---- Format address from Information.address object ----
function formatAddress(addr = {}) {
  const parts = [
    addr.house_number,
    addr.road ? `ถนน${addr.road}` : '',
    addr.subdistrict ? `ตำบล${addr.subdistrict}` : '',
    addr.district ? `อำเภอ${addr.district}` : '',
    addr.province ? `จังหวัด${addr.province}` : '',
    addr.postcode,
  ].filter(Boolean);
  return parts.join(' ');
}

// ---- Format number with commas ----
function fmt(val) {
  const n = parseFloat(val) || 0;
  return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---- Build the repeating header (logo + company + customer + info box) ----
function buildHeader(data) {
  const {
    company = {},
    customerName = '',
    customerAddress = '',
    customerTaxId = '',
    billingNumber = '',
    date,
    salesperson = '',
    referenceNumber = '',
    topic = '',
    contactName = '',
    contactEmail = '',
  } = data;

  const companyNameEng = company.name_eng || 'SOTUS ENGINEERING CO., LTD.';
  const companyNameThai = company.name || '';
  const companyAddr = company.address ? formatAddress(company.address) : '';
  const companyTaxId = company.tax_id ? `เลขประจำตัวผู้เสียภาษี ${company.tax_id}` : '';
  const companyPhone = company.phone ? `โทร. ${company.phone}` : '';

  // Right-side info table rows
  const infoRows = [
    ['เลขที่', billingNumber],
    ['วันที่', date ? dayjs(date).format('DD/MM/YYYY') : ''],
    ['ผู้ขาย', salesperson],
    ['อ้างอิง', referenceNumber],
  ];
  const infoRows2 = [
    ['ชื่องาน', topic],
    ['ผู้ติดต่อ', contactName],
    ['อีเมล', contactEmail],
  ];

  const labelStyle = { color: ORANGE, fontSize: 9, bold: true };
  const valueStyle = { fontSize: 9, color: DARK };

  const buildInfoTable = (rows) => ({
    table: {
      widths: [45, '*'],
      body: rows.map(([label, value]) => [
        { text: label, ...labelStyle, border: [false, false, false, false] },
        { text: value || '', ...valueStyle, border: [false, false, false, false] },
      ]),
    },
    layout: 'noBorders',
  });

  return [
    // Orange triangle top-right corner
    {
      canvas: [
        {
          type: 'polyline',
          points: [{ x: 0, y: 0 }, { x: 60, y: 0 }, { x: 60, y: 60 }],
          fillColor: ORANGE,
          closePath: true,
          lineWidth: 0,
        },
      ],
      absolutePosition: { x: 535, y: 0 },
    },

    // Row 1: Company English name (left) | ใบเสนอราคา (right)
    {
      margin: [40, 16, 40, 0],
      columns: [
        {
          width: '60%',
          stack: [
            { text: companyNameEng, fontSize: 16, bold: true, color: DARK },
            { text: 'Maintenance Solutions and Services', fontSize: 8, color: GRAY, italics: true },
          ],
        },
        {
          width: '40%',
          text: 'ใบเสนอราคา',
          fontSize: 20,
          bold: true,
          color: ORANGE,
          alignment: 'right',
        },
      ],
    },

    // Divider
    {
      margin: [40, 6, 40, 6],
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: BORDER_COLOR }],
    },

    // Row 2: Company info (left) + info box rows 1-4 (right)
    {
      margin: [40, 4, 40, 0],
      columns: [
        {
          width: '55%',
          stack: [
            companyNameThai ? { text: companyNameThai, fontSize: 9, bold: true, color: DARK } : null,
            companyAddr ? { text: companyAddr, fontSize: 9, color: DARK } : null,
            companyTaxId ? { text: companyTaxId, fontSize: 9, color: DARK } : null,
            companyPhone ? { text: companyPhone, fontSize: 9, color: DARK } : null,
          ].filter(Boolean),
        },
        {
          width: '45%',
          ...buildInfoTable(infoRows),
        },
      ],
    },

    // Row 3: ลูกค้า (left) + info box rows 5-7 (right)
    {
      margin: [40, 6, 40, 8],
      columns: [
        {
          width: '55%',
          stack: [
            { text: 'ลูกค้า', fontSize: 9, bold: true, color: ORANGE },
            customerName ? { text: customerName, fontSize: 9, bold: true, color: DARK } : null,
            customerAddress ? { text: customerAddress, fontSize: 9, color: DARK } : null,
            customerTaxId ? { text: `เลขประจำตัวผู้เสียภาษี ${customerTaxId}`, fontSize: 9, color: DARK } : null,
          ].filter(Boolean),
        },
        {
          width: '45%',
          ...buildInfoTable(infoRows2),
        },
      ],
    },

    // Bottom divider
    {
      margin: [40, 0, 40, 0],
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: BORDER_COLOR }],
    },
  ];
}

// ---- Build items table body ----
function buildItemsBody(items = []) {
  const header = [
    { text: '#', style: 'tableHeader', alignment: 'center' },
    { text: 'รายละเอียด', style: 'tableHeader' },
    { text: 'จำนวน', style: 'tableHeader', alignment: 'center' },
    { text: 'หน่วย', style: 'tableHeader', alignment: 'center' },
    { text: 'ราคาต่อหน่วย', style: 'tableHeader', alignment: 'right' },
    { text: 'ยอดรวม', style: 'tableHeader', alignment: 'right' },
  ];

  const rows = items.map((item, idx) => {
    const descStack = item.detail
      ? {
          stack: [
            { text: item.description || '', fontSize: 9, bold: true, color: DARK },
            { text: item.detail, fontSize: 8, color: GRAY },
          ],
        }
      : { text: item.description || '', fontSize: 9, color: DARK };

    return [
      { text: idx + 1, alignment: 'center', fontSize: 9, color: DARK },
      descStack,
      { text: fmt(item.quantity), alignment: 'center', fontSize: 9, color: DARK },
      { text: item.unit || '', alignment: 'center', fontSize: 9, color: DARK },
      { text: fmt(item.pricePerUnit), alignment: 'right', fontSize: 9, color: DARK },
      { text: fmt(item.total), alignment: 'right', fontSize: 9, color: DARK },
    ];
  });

  return [header, ...rows];
}

// ---- Main export ----
const quotationReport = (data) => {
  const {
    billingNumber = '',
    date,
    salesperson = '',
    referenceNumber = '',
    topic = '',
    customerName = '',
    customerAddress = '',
    customerTaxId = '',
    contactName = '',
    contactEmail = '',
    items = [],
    vatRate = 7,
    notes = '',
    internalNotes = '',
    summary = {},
    company = {},
  } = data;

  const { subtotal = 0, discount = 0, afterDiscount, vat = 0, total = 0 } = summary;
  const netAfterDiscount = afterDiscount !== undefined ? afterDiscount : (parseFloat(subtotal) - parseFloat(discount));

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'Portrait',
    pageMargins: [40, 205, 40, 55],

    defaultStyle: { font: 'Sarabun', fontSize: 10, color: DARK },

    header: () => buildHeader({
      company,
      customerName,
      customerAddress,
      customerTaxId,
      billingNumber,
      date,
      salesperson,
      referenceNumber,
      topic,
      contactName,
      contactEmail,
    }),

    footer: (currentPage, pageCount) => {
      const isLastPage = currentPage === pageCount;
      const items = [];

      if (isLastPage) {
        items.push({
          margin: [40, 4, 40, 0],
          columns: [
            {
              width: '50%',
              text: `ในนาม ${customerName}`,
              fontSize: 9,
              color: GRAY,
            },
            {
              width: '50%',
              text: `ในนาม ${company.name || ''}`,
              fontSize: 9,
              color: GRAY,
              alignment: 'right',
            },
          ],
        });

        items.push({
          margin: [40, 16, 40, 0],
          columns: [
            {
              width: '45%',
              stack: [
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 160, y2: 0, lineWidth: 0.5, lineColor: DARK }] },
                {
                  columns: [
                    { text: 'ผู้สั่งซื้อสินค้า', fontSize: 8, color: GRAY, width: '*' },
                    { text: 'วันที่', fontSize: 8, color: GRAY, width: 60, alignment: 'right' },
                  ],
                  margin: [0, 3, 0, 0],
                },
              ],
            },
            { width: '10%', text: '' },
            {
              width: '45%',
              stack: [
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 160, y2: 0, lineWidth: 0.5, lineColor: DARK }] },
                {
                  columns: [
                    { text: 'ผู้อนุมัติ', fontSize: 8, color: GRAY, width: '*' },
                    {
                      text: date ? dayjs(date).format('DD/MM/YYYY') : '',
                      fontSize: 8,
                      color: GRAY,
                      width: 60,
                      alignment: 'right',
                    },
                  ],
                  margin: [0, 3, 0, 0],
                },
              ],
            },
          ],
        });
      }

      items.push({
        margin: [40, isLastPage ? 6 : 4, 40, 0],
        text: `หน้าที่ ${currentPage}/${pageCount}`,
        fontSize: 8,
        color: GRAY,
        alignment: 'center',
      });

      return { stack: items };
    },

    content: [
      // Items table
      {
        margin: [0, 4, 0, 0],
        table: {
          headerRows: 1,
          widths: [20, '*', 40, 36, 72, 72],
          body: buildItemsBody(items),
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 0.5 : 0.3),
          vLineWidth: () => 0.3,
          hLineColor: () => BORDER_COLOR,
          vLineColor: () => BORDER_COLOR,
          fillColor: (rowIndex) => (rowIndex === 0 ? LIGHT_GRAY : null),
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
      },

      // Summary
      {
        margin: [0, 8, 0, 0],
        columns: [
          {
            width: '*',
            text: `(${bahtText(total)})`,
            fontSize: 9,
            color: DARK,
            italics: true,
            margin: [0, 16, 0, 0],
          },
          {
            width: 280,
            table: {
              widths: ['*', 100],
              body: [
                [
                  { text: 'รวมเป็นเงิน', fontSize: 9, alignment: 'right', border: [false, false, false, false], color: GRAY },
                  { text: `${fmt(subtotal)} บาท`, fontSize: 9, alignment: 'right', border: [false, false, false, false], bold: true },
                ],
                parseFloat(discount) > 0 ? [
                  { text: 'ส่วนลด', fontSize: 9, alignment: 'right', border: [false, false, false, false], color: GRAY },
                  { text: `${fmt(discount)} บาท`, fontSize: 9, alignment: 'right', border: [false, false, false, false] },
                ] : null,
                [
                  { text: `ภาษีมูลค่าเพิ่ม ${vatRate}%`, fontSize: 9, alignment: 'right', border: [false, false, false, false], color: GRAY },
                  { text: `${fmt(vat)} บาท`, fontSize: 9, alignment: 'right', border: [false, false, false, false] },
                ],
                [
                  {
                    text: 'จำนวนเงินรวมทั้งสิ้น',
                    fontSize: 10,
                    bold: true,
                    alignment: 'right',
                    border: [false, true, false, false],
                    color: DARK,
                  },
                  {
                    text: `${fmt(total)} บาท`,
                    fontSize: 10,
                    bold: true,
                    alignment: 'right',
                    border: [false, true, false, false],
                    color: ORANGE,
                  },
                ],
              ].filter(Boolean),
            },
            layout: { hLineColor: () => BORDER_COLOR, vLineColor: () => BORDER_COLOR },
          },
        ],
      },

      // Notes
      notes ? { text: 'หมายเหตุ', fontSize: 10, bold: true, color: ORANGE, margin: [0, 16, 0, 4] } : null,
      notes ? { text: notes, fontSize: 9, color: DARK } : null,
    ].filter(Boolean),

    styles: {
      tableHeader: {
        bold: true,
        fontSize: 9,
        color: DARK,
        fillColor: LIGHT_GRAY,
      },
    },
  };

  try {
    pdfMake.createPdf(docDefinition).open();
  } catch (error) {
    console.error('Error creating quotation PDF:', error);
    throw new Error('ไม่สามารถสร้าง PDF ได้');
  }
};

export default quotationReport;
